import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type RequisitoCargo = components['schemas']['RequisitoCargoRead']
export type RequisitoQuitado = components['schemas']['QuitadoRead']
export type BloqueCargos = components['schemas']['ConfirmacionBloqueRead']
export type CargoDelBloque = components['schemas']['CargoDelBloqueRead']
export type AlcanceBloque = components['schemas']['AlcanceBloqueRead']
export type LoteConfirmado = components['schemas']['LoteRead']

export const REQUISITOS_POR_PAGINA = 10

function base(businessId: string | undefined, positionId: string | undefined) {
  return `/businesses/${businessId}/sgsst/positions/${positionId}/requisitos`
}

function requisitosKey(businessId: string | undefined, positionId: string | undefined) {
  return ['requisitos-cargo', businessId, positionId]
}

function bloqueKey(businessId: string | undefined) {
  return ['perfiles-cargo-bloque', businessId]
}

/** Incluye las filas que van a nacer (`id: null`): condiciones confirmadas después del perfil. */
export function useRequisitosCargo(businessId: string | undefined, positionId: string | undefined, page: number) {
  return usePagedList<RequisitoCargo>(
    requisitosKey(businessId, positionId),
    businessId && positionId ? base(businessId, positionId) : undefined,
    {page, search: ''},
    REQUISITOS_POR_PAGINA
  )
}

/** Arranca el reloj de la fila; sin periodicidad verificada no la pinta en verde. */
export function useConfirmarRequisito(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (requisitoId: string) =>
      (await api.post<RequisitoCargo>(`${base(businessId, positionId)}/${requisitoId}/confirmar`, {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: requisitosKey(businessId, positionId)})
    },
  })
}

export function useQuitarRequisito(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {requisitoId: string; motivo: string}) =>
      (
        await api.delete<RequisitoQuitado>(`${base(businessId, positionId)}/${input.requisitoId}`, {
          data: {motivo: input.motivo},
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: requisitosKey(businessId, positionId)})
    },
  })
}

/** No se pagina: el orden es la propuesta y paginarla escondería la mitad que menos mueve. */
export function useBloqueCargos(businessId: string | undefined) {
  return useQuery({
    queryKey: bloqueKey(businessId),
    queryFn: async () => (await api.get<BloqueCargos>(`/businesses/${businessId}/sgsst/perfiles-cargo/lotes`)).data,
    enabled: !!businessId,
  })
}

/** Mutación y no consulta: cada llamada emite un `alcance_token` nuevo con su propio reloj. */
export function useAlcanceBloque(businessId: string) {
  return useMutation({
    mutationFn: async (positionIds: string[]) =>
      (
        await api.post<AlcanceBloque>(`/businesses/${businessId}/sgsst/perfiles-cargo/lotes/alcance`, {
          position_ids: positionIds,
        })
      ).data,
  })
}

export function useConfirmarBloque(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {positionIds: string[]; exigibleDesde: string; motivo: string; alcanceToken: string}) =>
      (
        await api.post<LoteConfirmado>(`/businesses/${businessId}/sgsst/perfiles-cargo/lotes/confirmar`, {
          position_ids: input.positionIds,
          exigible_desde: input.exigibleDesde,
          motivo: input.motivo || null,
          alcance_token: input.alcanceToken,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: bloqueKey(businessId)})
      queryClient.invalidateQueries({queryKey: ['perfiles-cargo', businessId]})
      track('perfiles_cargo_bloque_confirmado', {business_id: businessId, confirmados: data.confirmados})
    },
  })
}
